import React from "react";
import PropTypes from "prop-types";
import { Field, FieldArray, useFormikContext } from "formik";
import {
  TextInput,
  SelectInput,
  RadioGroupField,
  PhoneNumberInput
} from "components/form";
import useTranslate from "hooks/use-translate-language";
import { formatDate, isValidPhone, isValidZip } from "utils/misc";
import { STATES } from "utils/enum";

const getInsuranceDisplayName = index => {
  switch (index) {
    case 0:
      return "primary";
    case 1:
      return "secondary";
    case 2:
      return "tertiary";
    default:
      return "";
  }
};

// form_lists/insurance_relationship_types
const guarantorOptions = [
  "Self",
  "Spouse",
  "Child",
  "Other Adult",
  "Unspecified Dependent",
  "Cadaver Donor",
  "Organ Donor",
  "Employee",
  "Life Partner"
].map(x => ({
  text: x,
  value: x
}));

const InsuranceForm = ({ insuranceOptions, patientInsurances, questions }) => {
  const translate = useTranslate();

  const insurnace_change_question = questions.find(
    q => q.id === "insurance_change"
  );

  const { values, errors, setFieldValue } = useFormikContext();

  const disabled = !values.questions?.insurance_change;

  React.useEffect(() => {
    if (values.questions?.insurance_change === false) {
      setFieldValue("insurances", patientInsurances);
    }
  }, [values.questions?.insurance_change]);
  if (!insurnace_change_question) {
    return null;
  }
  return (
    <div>
      <Field
        component={RadioGroupField}
        name="questions.insurance_change"
        label={translate("insuranceHasChanged")}
        value={values.questions.insurance_change}
        validate={x => (typeof x === "boolean" ? undefined : "Required")}
        options={[
          { value: true, text: "Yes" },
          { value: false, text: "No" }
        ]}
      />
      <FieldArray
        name="insurances"
        render={arrayHelpers => (
          <div style={{ marginTop: 20 }}>
            {values.insurances?.map((_, index) => (
              <div key={index} style={{ padding: 5 }}>
                {errors.insurances && errors.insurances[index] && (
                  <div style={{ color: "red" }}>
                    {errors.insurances[index].insurance_company_id}
                  </div>
                )}
                <SelectInput
                  name={`insurances.${index}.insurance_company_id`}
                  label={translate(
                    getInsuranceDisplayName(index) + "Insurance"
                  )}
                  options={[
                    { text: "", value: "", key: "" },
                    ...insuranceOptions
                  ]}
                  style={{ marginTop: 10, marginBottom: 10, marginLeft: 3 }}
                  value={values.insurances[index]?.insurance_company_id}
                  disabled={disabled}
                />
                {/* <SearchableDropdown */}
                {/*   options={[ */}
                {/*     { text: "", value: "", key: "" }, */}
                {/*     ...insuranceOptions */}
                {/*   ]} */}
                {/*   selected={values.insurances[index]?.insurance_company_id} */}
                {/*   disabled={disabled} */}
                {/*   onSelect={value => { */}
                {/*     const insuranceCompany = insuranceOptions?.find( */}
                {/*       x => x?.value === value */}
                {/*     ); */}
                {/*     setFieldValue(`insurances.${index}`, { */}
                {/*       ...values.insurances[index], */}
                {/*       insurance_company_id: value, */}
                {/*       insurance_company_name: insuranceCompany?.text, */}
                {/*       primary_order: index + 1, */}
                {/*       requires_dysfunctions: */}
                {/*         insuranceCompany?.requires_dysfunctions */}
                {/*     }); */}
                {/*   }} */}
                {/* /> */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    alignItems: "center",
                    gridGap: 10
                  }}
                >
                  <TextInput
                    name={`insurances.${index}.policy_number`}
                    type="text"
                    label={translate("policyNumber")}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                  />
                  <TextInput
                    name={`insurances.${index}.group_number`}
                    type="text"
                    label={translate("groupNumber")}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                  />

                  <PhoneNumberInput
                    name={`insurances.${index}.insurance_phone`}
                    type="tel"
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    label={translate("insurancePhoneNumber")}
                    validate={isValidPhone}
                  />

                  <TextInput
                    name={`insurances.${index}.policy_start_date`}
                    type="date"
                    label={translate("policyStartDate")}
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                    value={formatDate(
                      values.insurances[index].policy_start_date,
                      "YYYY-MM-DD"
                    )}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    alignItems: "center",
                    gridGap: 10,
                    marginTop: 5
                  }}
                >
                  <TextInput
                    name={`insurances.${index}.guarantor_name`}
                    type="text"
                    disabled={disabled}
                    label={translate("guarantorName")}
                  />
                  <SelectInput
                    name={`insurances.${index}.guarantor_relation`}
                    component="select"
                    label={translate("guarantor")}
                    InputLabelProps={{ shrink: true }}
                    style={{ marginTop: 10, marginBottom: 10, marginLeft: 3 }}
                    disabled={disabled}
                    options={guarantorOptions}
                  />
                  <TextInput
                    name={`insurances.${index}.guarantor_dob`}
                    type="date"
                    disabled={disabled}
                    label={translate("guarantorDob")}
                    InputLabelProps={{ shrink: true }}
                    value={formatDate(
                      values.insurances[index].guarantor_dob,
                      "YYYY-MM-DD"
                    )}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridGap: 10
                  }}
                >
                  <TextInput
                    name={`insurances.${index}.insurance_address.street_address`}
                    type="text"
                    disabled={disabled}
                    label="Insurance Street Address"
                  />

                  <TextInput
                    name={`insurances.${index}.insurance_address.street_address2`}
                    type="text"
                    disabled={disabled}
                    label="Insurance Street Address 2"
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gridGap: 10
                  }}
                >
                  <TextInput
                    name={`insurances.${index}.insurance_address.city`}
                    type="text"
                    disabled={disabled}
                    label="Insurance City"
                  />
                  <SelectInput
                    name={`insurances.${index}.insurance_address.state`}
                    component="select"
                    disabled={disabled}
                    label="Insurance State"
                    options={STATES}
                    style={{ height: "100%" }}
                  />
                  <TextInput
                    name={`insurances.${index}.insurance_address.zip`}
                    type="text"
                    disabled={disabled}
                    label="Insurance Zip"
                    validate={isValidZip}
                  />
                </div>

                <div>
                  {index > 0 && !disabled && (
                    <div
                      style={{
                        display: "flex",
                        flex: 1,
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <button onClick={() => arrayHelpers.remove(index)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                {index == 0 && <hr style={{ margin: "5px 0px" }} />}
              </div>
            ))}
            {values.insurances.length == 1 && !disabled && (
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <button
                  type="button"
                  style={{ margin: 0, padding: "5px 15px" }}
                  onClick={() =>
                    arrayHelpers.push({
                      insurance_company_id: "",
                      plan_number: "",
                      group_number: "",
                      guarantor_relation: "",
                      policy_start_date: "",
                      primary_order: values.insurances.length + 1
                    })
                  }
                >
                  Add Secondary Insurance
                </button>
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
};

InsuranceForm.propTypes = {
  patientInsurances: PropTypes.arrayOf(PropTypes.object).isRequired,
  insuranceOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default InsuranceForm;
